import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './Tooltip.scss';
import clsx from 'clsx';
import { DISPATCH_EVENTS, KEYBOARD_EVENTS } from '../../../utils/constants/commonConstants';
import { closestScrollContainer, pickBesideSide } from './tooltipPlacement';
/**
 * @Description Custom Tooltip component to display information on hover
 * @param content Tooltip message content
 * @param direction Tooltip position with respect to target element
 * @param children Elements for which the Tooltip is to be shown on hover
 * @param usePortal Whether to render tooltip in a React Portal (renders to document.body)
 *
 * ## When to use `usePortal={true}`:
 *
 * Use Portal rendering when the tooltip needs to escape CSS constraints:
 *
 * 1. **Parent has `overflow: hidden/auto/scroll`**
 *    - Tooltip gets clipped by scrollable containers
 *    - Example: Tables, modals, sidebars with scrolling
 *
 * 2. **Complex stacking contexts**
 *    - Tooltip appears behind other elements despite high z-index
 *    - Parent elements have `position: sticky`, `position: fixed`, or `z-index` values
 *    - Example: Sticky table headers, fixed navigation bars
 *
 * 3. **Nested positioned elements**
 *    - Tooltip is inside multiple layers of positioned elements
 *    - z-index doesn't work across stacking contexts
 *
 * ## When NOT to use `usePortal`:
 *
 * - Simple tooltips in normal document flow (default behavior is sufficient)
 * - No overflow or z-index issues present
 * - Performance-critical scenarios (portal adds minimal overhead but avoid if unnecessary)
 *
 * @example
 * // Regular tooltip (no portal needed)
 * <Tooltip content="Simple tooltip" direction="top">
 *   <button>Hover me</button>
 * </Tooltip>
 *
 * @example
 * // Portal tooltip (escapes table overflow)
 * <Tooltip content="Tooltip in table" direction="top" usePortal={true}>
 *   <TableCell>Content</TableCell>
 * </Tooltip>
 */
interface TooltipProps {
    content: string | React.ReactNode;
    direction: 'top' | 'right' | 'bottom' | 'left';
    isBenchmarking?: boolean;
    alwaysVisible?: boolean;
    inverted?: boolean;
    small?: boolean;
    containerFullWidth?: boolean;
    fitContent?: boolean;
    containerFullHeight?: boolean;
    children?: React.ReactNode;
    height?: string;
    style?: React.CSSProperties;
    className?: string;
    isDealNotesTooltip?: boolean;
    isOverviewDetail?: boolean;
    hideDelay?: number;
    /**
     * Delay in ms before the tooltip opens on hover, so brushing past a trigger on the way
     * somewhere else doesn't flash it. The pending open is cancelled if the pointer leaves first,
     * or if another portal tooltip opens. Click/tap and keyboard activation ignore it — those are
     * deliberate, so there is no hover intent to wait for.
     * @default 0
     */
    showDelay?: number;
    flexible?: boolean;
    contentCss?: React.CSSProperties;
    /**
     * Render tooltip in a React Portal to escape overflow clipping and stacking context issues.
     * Set to `true` when tooltip appears behind other elements or gets clipped by parent containers.
     * @default false
     */
    usePortal?: boolean;
    /**
     * Called every time the tooltip transitions to visible (hover/focus). Useful for lazy-fetching
     * content on demand. Callers are responsible for their own dedup if needed.
     */
    onShow?: () => void;
    /**
     * Suppress the built-in CSS arrow (`::before`). Use when the design has no arrow indicator.
     * @default false
     */
    hideArrow?: boolean;
    /**
     * Do not dispatch `TOOLTIP_PORTAL_SHOW` when this tooltip opens. Use when two related portal
     * tooltips need to coexist (e.g. a name tooltip nested inside an overview tooltip on the same
     * trigger) — without this, each would close the other on hover.
     * @default false
     */
    disablePortalShowDispatch?: boolean;
    /**
     * Portal-only: anchor the tip to the pointer and track the cursor instead of the trigger's box.
     * @default false
     */
    followCursor?: boolean;
    /**
     * Open on click/tap instead of hover. Needed on touch devices where hover doesn't fire.
     * Toggles on trigger click and closes on any click outside the trigger or the tip.
     * @default false
     */
    triggerOnClick?: boolean;
    /**
     * Extra class added to the outer `.Tooltip-Tip` element. Use for static styling overrides
     * (size, background, radius) instead of inline `style` — keeps presentation in `.module.scss`.
     */
    tipClassName?: string;
    /**
     * Extra class added to the inner `.Content` element. Use for static styling overrides
     * (padding, background, border-radius, box-shadow) instead of inline `contentCss`.
     */
    contentClassName?: string;
    /** Caps tip width, e.g. calc(100vw - 1.5rem) on phones. */
    maxWidth?: string;
    /** Viewport gap in px kept by the portal clamp (default 8). */
    edgePadding?: number;
    /** Extra class on the outer `.Tooltip-Wrapper` — e.g. to enlarge the tap area without resizing the trigger. */
    wrapperClassName?: string;
    /**
     * Opt-in keyboard accessibility for hover-only tooltips: makes the wrapper focusable
     * (`tabIndex=0`) and reveals the tooltip on focus / hides it on blur, in addition to the
     * existing hover behaviour (unchanged). Also gives the wrapper an `aria-label` derived from
     * `content` when it's a plain string, so the trigger has an accessible name. Does not add any
     * click/toggle behaviour — pairs with a trigger that has no other affordance of its own (e.g.
     * a standalone info/warning icon), not with `triggerOnClick`.
     * @default false
     */
    focusable?: boolean;
    /** Accessible name for the wrapper when it is a focus stop (`focusable` / `triggerOnClick`). Overrides the name derived from string `content`; pass it when `content` is a node. @default undefined (unchanged behaviour) */
    ariaLabel?: string;
    /** Portal + left/right only: open on the other side of the trigger when the preferred side lacks room. */
    flipToFit?: boolean;
    /** Portal only: clamp vertically inside the trigger's scroll container, e.g. to stay below a fixed top bar. */
    keepInScrollContainer?: boolean;
    /** Portal + left/right only: `center` centres the tip on the trigger vertically instead of top-aligning it. */
    verticalAlign?: 'top' | 'center';
    /** Stop clicks inside the tip reaching the trigger's ancestors, e.g. a clickable row — a portal tip still bubbles through them in React. */
    containTipClicks?: boolean;
    /** Escape closes an open tooltip; focus inside the tip returns to the trigger without re-opening it. @default false */
    closeOnEscape?: boolean;
}

/** Only the fields the portal positioning reads — a DOMRect satisfies it, as does a pointer. */
type Rect = { top: number; bottom: number; left: number; right: number; width: number };

const Tooltip = ({
    content = '',
    direction = 'top',
    children,
    alwaysVisible,
    inverted,
    small,
    containerFullWidth,
    fitContent,
    containerFullHeight,
    isBenchmarking,
    height = '',
    style,
    className,
    isDealNotesTooltip,
    isOverviewDetail,
    hideDelay = 0,
    showDelay = 0,
    flexible = false,
    contentCss,
    usePortal = false,
    followCursor = false,
    onShow,
    hideArrow = false,
    disablePortalShowDispatch = false,
    triggerOnClick = false,
    tipClassName,
    contentClassName,
    maxWidth,
    edgePadding = 8,
    wrapperClassName,
    focusable = false,
    ariaLabel,
    flipToFit = false,
    keepInScrollContainer = false,
    verticalAlign = 'top',
    containTipClicks = false,
    closeOnEscape = false,
}: TooltipProps) => {
    const [active, setActive] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const [clampTick, setClampTick] = useState(0);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const showTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const moveFrameRef = useRef<number | null>(null);
    const [placement, setPlacement] = useState(direction);
    const anchorRectRef = useRef<Rect | null>(null);
    const suppressFocusOpenRef = useRef(false);

    useEffect(() => {
        if (alwaysVisible) setActive(true);
    }, [alwaysVisible]);

    useEffect(() => {
        return () => {
            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
            }
            if (showTimeoutRef.current) {
                clearTimeout(showTimeoutRef.current);
            }
            if (moveFrameRef.current !== null) {
                cancelAnimationFrame(moveFrameRef.current);
            }
        };
    }, []);

    /* 
        Listen for any other portal tooltip opening, and force-close ourselves.
        Decoupled cross-instance coordination via DOM event — no shared module state.
    */
    useEffect(() => {
        if (!usePortal) return;
        const handler = (e: Event) => {
            if ((e as CustomEvent).detail === wrapperRef.current) return; // it's us opening
            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
                hideTimeoutRef.current = null;
            }
            clearPendingShow();
            setActive(false);
        };
        document.addEventListener(DISPATCH_EVENTS.TOOLTIP_PORTAL_SHOW, handler);
        return () => document.removeEventListener(DISPATCH_EVENTS.TOOLTIP_PORTAL_SHOW, handler);
    }, [usePortal]);

    // Keep portal tooltip inside the viewport on all 4 edges.
    useLayoutEffect(() => {
        if (!active || !usePortal || !tooltipRef.current) return;
        const r = tooltipRef.current.getBoundingClientRect();
        const PAD = edgePadding;
        const anchor = anchorRectRef.current;
        if (flipToFit && anchor && (placement === 'left' || placement === 'right')) {
            const gap = placement === 'right' ? r.left - anchor.right : anchor.left - r.right;
            const side = pickBesideSide(placement, anchor, r.width + gap, window.innerWidth, PAD);
            if (side !== placement) {
                setPlacement(side);
                setTooltipPosition(positionFrom(anchor, side));
                return;
            }
        }
        const container = keepInScrollContainer ? closestScrollContainer(wrapperRef.current)?.getBoundingClientRect() : undefined;
        const fitsContainer =
            !!container && r.height <= Math.min(window.innerHeight, container.bottom) - Math.max(0, container.top) - 2 * PAD;
        const bounds = fitsContainer ? container : undefined;
        const minTop = Math.max(0, bounds?.top ?? 0) + PAD;
        const maxBottom = Math.min(window.innerHeight, bounds?.bottom ?? window.innerHeight) - PAD;
        const dx = Math.max(PAD - r.left, Math.min(0, window.innerWidth - PAD - r.right));
        const dy = Math.max(minTop - r.top, Math.min(0, maxBottom - r.bottom));
        if (dx || dy) setTooltipPosition((p) => ({ top: p.top + dy, left: p.left + dx }));
    }, [active, usePortal, clampTick, edgePadding, placement]);

    /*
        followCursor anchors on a zero-width box at the pointer, which collapses every direction to
        the same point — so open and move MUST share this mapping or the tip jumps on first move.
        position: fixed → coordinates stay in viewport space (no scroll offsets).
    */
    const anchorRect = (e?: React.MouseEvent): Rect =>
        followCursor && e
            ? { top: e.clientY, bottom: e.clientY, left: e.clientX, right: e.clientX, width: 0 }
            : (wrapperRef.current as HTMLDivElement).getBoundingClientRect();

    const besideTop = (rect: Rect) => (verticalAlign === 'center' ? (rect.top + rect.bottom) / 2 : rect.top);

    const positionFrom = (rect: Rect, side = placement) =>
        side === 'right'
            ? { top: besideTop(rect), left: rect.right } // Right edge of trigger
            : side === 'left'
            ? { top: besideTop(rect), left: rect.left } // Left edge of trigger
            : side === 'bottom'
            ? { top: rect.bottom, left: rect.left + rect.width / 2 } // Below, centered
            : { top: rect.top, left: rect.left + rect.width / 2 }; // Above, centered

    const clearPendingShow = () => {
        if (showTimeoutRef.current) {
            clearTimeout(showTimeoutRef.current);
            showTimeoutRef.current = null;
        }
    };

    /*
        The open itself, split out of showTip so a delayed open can run it later.
        Takes the anchor rect rather than the event: under showDelay the React event is pooled long
        before this runs. Everything here is deferred with the open — dispatching TOOLTIP_PORTAL_SHOW
        early would close a sibling portal tooltip for a hover that never opens, and onShow is
        documented as firing when the tooltip becomes visible, which lazy-fetching callers rely on.
    */
    const revealTip = (rect: Rect) => {
        // Portal rendering: Calculate absolute position relative to viewport
        // This is needed because portal renders to document.body, outside the normal DOM hierarchy
        if (usePortal && wrapperRef.current) {
            if (!disablePortalShowDispatch) {
                document.dispatchEvent(new CustomEvent(DISPATCH_EVENTS.TOOLTIP_PORTAL_SHOW, { detail: wrapperRef.current }));
            }

            anchorRectRef.current = rect;
            setPlacement(direction);
            setTooltipPosition(positionFrom(rect, direction));
            setClampTick((t) => t + 1); // Force viewport clamp to re-run for the new position
        }

        onShow?.();
        setActive(true);
    };

    const showTip = (e?: React.MouseEvent) => {
        if (alwaysVisible) return;
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }

        const rect = anchorRect(e); // read now — the React event is pooled

        if (showDelay > 0 && !active) {
            clearPendingShow();
            showTimeoutRef.current = setTimeout(() => {
                showTimeoutRef.current = null;
                revealTip(rect);
            }, showDelay);
            return;
        }

        revealTip(rect);
    };

    /*
        Reposition only — showTip would re-dispatch TOOLTIP_PORTAL_SHOW and re-fire onShow per pixel.
        Coalesced into one frame: a high-poll mouse fires mousemove faster than the display paints,
        and each update makes the clamp effect read the tip's rect (a forced layout).
    */
    const moveTip = (e: React.MouseEvent) => {
        if (alwaysVisible || !active) return;
        const next = positionFrom(anchorRect(e)); // read now — the React event is pooled
        if (moveFrameRef.current !== null) return;
        moveFrameRef.current = requestAnimationFrame(() => {
            moveFrameRef.current = null;
            setTooltipPosition(next);
            setClampTick((t) => t + 1);
        });
    };

    const hideTip = () => {
        if (alwaysVisible) return;
        clearPendingShow();
        if (hideDelay > 0) {
            hideTimeoutRef.current = setTimeout(() => {
                setActive(false);
            }, hideDelay);
        } else {
            setActive(false);
        }
    };

    // Click-to-toggle (touch devices). Opens immediately: showDelay exists to detect hover intent,
    // and a tap has already declared it.
    const toggleTip = () => {
        clearPendingShow();
        if (active) setActive(false);
        else revealTip(anchorRect());
    };

    // Keyboard support for click-triggered tooltips: Enter/Space toggles like a click,
    // Escape dismisses. Matches the role="button" + tabIndex + onKeyDown pattern used
    // for other click-toggle triggers in this codebase (e.g. LookalikeTable's EntityPill).
    const onTriggerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === KEYBOARD_EVENTS.ENTER || e.key === KEYBOARD_EVENTS.SPACE) {
            e.preventDefault();
            toggleTip();
        } else if (e.key === KEYBOARD_EVENTS.ESCAPE && active) {
            setActive(false);
        }
    };

    const onOutside = useCallback((e: MouseEvent) => {
        const target = e.target as Node;
        if (wrapperRef.current?.contains(target)) return;
        if (tooltipRef.current?.contains(target)) return;
        setActive(false);
    }, []);

    // While a click-triggered tooltip is open, close it on any click outside the
    // trigger or the tip itself. The opening click already finished before this
    // listener attaches, so it won't self-close.
    useEffect(() => {
        if (!triggerOnClick || !active) return;
        document.addEventListener('mousedown', onOutside);
        return () => document.removeEventListener('mousedown', onOutside);
    }, [triggerOnClick, active, onOutside]);

    useEffect(() => {
        if (!closeOnEscape || !active) return;
        const onEscape = (e: KeyboardEvent) => {
            if (e.key !== KEYBOARD_EVENTS.ESCAPE) return;
            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
                hideTimeoutRef.current = null;
            }
            clearPendingShow();
            if (tooltipRef.current?.contains(document.activeElement)) {
                suppressFocusOpenRef.current = true;
                wrapperRef.current?.focus();
            }
            setActive(false);
        };
        document.addEventListener('keydown', onEscape);
        return () => document.removeEventListener('keydown', onEscape);
    }, [closeOnEscape, active]);

    const onWrapperFocus = () => {
        if (suppressFocusOpenRef.current) {
            suppressFocusOpenRef.current = false;
            return;
        }
        showTip();
    };

    const renderTooltipContent = () => {
        const tooltipStyles: React.CSSProperties = {
            color: inverted ? 'white' : '#979797',
            wordBreak: 'break-word',
            whiteSpace: inverted ? 'nowrap' : 'pre-wrap',
            width: fitContent ? 'fit-content' : '',
            minWidth: small ? '80px' : '200px',
            maxWidth: maxWidth || undefined,
            height: height || 'fit-content',
            overflowY: height ? 'auto' : 'hidden',
            overflowX: 'hidden',
            display: content ? 'inline-block' : 'none',
            zIndex: 1000,
            scrollbarWidth: isOverviewDetail ? 'none' : 'auto',
            ...style,
        };

        // Portal-specific positioning: Use fixed positioning to escape parent constraints
        // This allows the tooltip to appear above overflow:hidden containers and complex stacking contexts
        if (usePortal) {
            tooltipStyles.position = 'fixed'; // Fixed relative to viewport, not parent
            tooltipStyles.top = `${tooltipPosition.top}px`; // Absolute position from viewport top
            tooltipStyles.left = `${tooltipPosition.left}px`; // Absolute position from viewport left
            tooltipStyles.zIndex = 999999; // Very high z-index to appear above all elements
            // A tip sitting under the cursor would steal the hover from its own trigger and flicker.
            if (followCursor) tooltipStyles.pointerEvents = 'none';
            const besideY = verticalAlign === 'center' ? '-50%' : '0';
            tooltipStyles.transform =
                placement === 'right'
                    ? `translate(0.5rem, ${besideY})` // Sit to the right of the trigger with an 8px gap
                    : placement === 'left'
                    ? `translate(calc(-100% - 0.5rem), ${besideY})` // Sit to the left of the trigger with an 8px gap
                    : placement === 'bottom'
                    ? 'translate(-50%, 0.625rem)' // Center and position below
                    : 'translate(-50%, calc(-100% - 0.625rem))'; // Center and position above (default/top)
        }

        return (
            <div
                ref={tooltipRef}
                className={clsx(
                    'Tooltip-Tip',
                    // Portal mode positions via inline fixed coords; direction classes carry
                    // `!important` rules that would override that, so skip them when portaling.
                    !usePortal && direction,
                    {
                        DealNotesTooltipBottom: !usePortal && isDealNotesTooltip && direction === 'bottom',
                        BenchmarkTooltip: isBenchmarking,
                        NoArrow: hideArrow,
                    },
                    tipClassName,
                    className,
                )}
                style={tooltipStyles}
                // In portal mode this div is detached from the wrapper's DOM subtree (rendered via
                // createPortal to document.body), so moving the mouse off the trigger and onto the
                // tip itself would otherwise fire the wrapper's onMouseLeave and close it mid-crossing
                // — before the mouse ever reaches interactive content (e.g. a button) inside the tip.
                // No-op for non-portal tooltips, where the tip is already a DOM child of the wrapper.
                onMouseEnter={usePortal && !triggerOnClick ? showTip : undefined}
                onMouseLeave={usePortal && !triggerOnClick ? hideTip : undefined}
                onClick={containTipClicks ? (e) => e.stopPropagation() : undefined}
            >
                <div className={clsx('Content', { OverviewDetail: isOverviewDetail }, contentClassName)} style={contentCss}>
                    {content}
                </div>
            </div>
        );
    };

    return (
        <div
            ref={wrapperRef}
            className={clsx('Tooltip-Wrapper', wrapperClassName)}
            onMouseEnter={triggerOnClick ? undefined : showTip}
            onMouseMove={followCursor && usePortal && !triggerOnClick ? moveTip : undefined}
            onMouseLeave={triggerOnClick ? undefined : hideTip}
            onClick={triggerOnClick ? toggleTip : undefined}
            onKeyDown={triggerOnClick ? onTriggerKeyDown : undefined}
            onFocus={focusable && !triggerOnClick ? onWrapperFocus : undefined}
            onBlur={focusable && !triggerOnClick ? hideTip : undefined}
            // `generic` can't be named (ARIA 1.2), so a labelled focus stop needs a role that can.
            role={triggerOnClick ? 'button' : focusable && ariaLabel ? 'group' : undefined}
            tabIndex={triggerOnClick || focusable ? 0 : undefined}
            aria-label={ariaLabel ?? (focusable && !triggerOnClick && typeof content === 'string' ? content : undefined)}
            aria-expanded={triggerOnClick ? active : undefined}
            style={{
                width: containerFullWidth ? '100%' : '',
                height: containerFullHeight ? '100%' : '',
            }}
        >
            {children}
            {/* Normal rendering: Tooltip is a child of the wrapper (respects parent CSS) */}
            {active && !usePortal && renderTooltipContent()}
            {/* Portal rendering: Tooltip renders to document.body (escapes parent CSS constraints) */}
            {active && usePortal && createPortal(renderTooltipContent(), document.body)}
        </div>
    );
};

export default Tooltip;
